import { GLGlobal, GLServiceBase } from "gl-commonui";
import { injectable, lazyInject } from "@app/util/di";
import { TYPES } from "@app/service/types";
import { IUserService } from "@app/service/users/index";
import { getEncryptedResourcesForUser } from "gl-commonui";
import {
    INotificationService,
    NotificationRequest,
    GetNotificationRequest,
} from "./index";
import { isAllTargetTypeSupportedForRole, NotificationTargetTypes } from "./model";
import { hydrateUserTargets } from "./notification-user-hydration";
import { resolveWriteRcc, resolveOwnerRcc, appendRegionOverride, isCurrentSiteRussia, logNotificationTenantDebug, NotificationRoutingContext } from "@app/util/notification-tenant-guard";

// Notification scope: addressed to a set of Roles, or to explicit Users.
const SCOPE_ROLES = 0;
const SCOPE_USERS = 1;

// IndividualUser role/target-type codes (shared numeric enums, unchanged across services).
const ROLE_INDIVIDUAL_USER = 89;
const TARGET_TYPE_INDIVIDUAL_USER = 5;

// Bulletin-board channels shown in the inbox/bell by default.
const DEFAULT_CHANNELS = [0, 2];

// The user lookup passes ids on the query string, so long recipient lists are
// split into batches to keep each URL within a sane length.
const USER_LOOKUP_BATCH_SIZE = 150;

const toPageIndex = (offset: number, pageSize: number) =>
    pageSize > 0 ? Math.floor((offset || 0) / pageSize) + 1 : 1;

const toNumberOrNull = (v: any) =>
    v === null || v === undefined || v === "" ? null : Number(v);

/**
 * Notification API adapter.
 *
 * Only the inbox + CRUD paths moved to the notification service
 * (serviceRoute.prefix). Target count and region lookups are still served by the
 * admin service and are reached with the "~" absolute-path override; recipient
 * details come from the account service through UserService. Gateway routing
 * itself is handled by gl-commonui.
 *
 * Calls that did not move keep their original request/response contract
 * verbatim. Only the notification-service calls are translated: this class keeps
 * the legacy method surface and shapes the pages/bell already consume
 * (roleTargets/targetType/targetId, {data,totalCount} paging,
 * {notification,notifiedCount} detail) and maps them to/from the new contract
 * (roleScopes/scopeType/scopeId, my-notifications paging), so the UI needs no
 * rewrite.
 */
@injectable()
export class NotificationService
    extends GLServiceBase<NotificationRequest, GetNotificationRequest>
    implements INotificationService
{
    serviceRoute = {
        prefix: "notification/v1/notifications",
        itemSuffix: "{id}",
    };

    @lazyInject(TYPES.IUserService)
    userService: IUserService;

    constructor() {
        super();
        const origPost = this.request.post.bind(this.request);
        const origPut = this.request.put.bind(this.request);

        this.request.post = async (url: string, data?: any) => {
            const enriched = this.isNotificationServiceUrl(url)
                ? await this.injectEncryptedResources(data)
                : data;
            return origPost(url, enriched);
        };

        this.request.put = async (url: string, data?: any) => {
            const enriched = this.isNotificationServiceUrl(url)
                ? await this.injectEncryptedResources(data)
                : data;
            return origPut(url, enriched);
        };
    }

    private isNotificationServiceUrl(url: string): boolean {
        return !!url && url.includes("notification/v1/notifications");
    }

    private getAdminBaseUrl(): string {
        const serviceUrl = this.url("~admin/v1");
        return (this.request as any).buildRequestUrl({ Url: serviceUrl, Prefix: "" });
    }

    private async injectEncryptedResources(payload: any): Promise<any> {
        if (!payload) return payload;
        const userId = GLGlobal.loginInfo().profile.sub;
        const enc = await getEncryptedResourcesForUser(userId, this.getAdminBaseUrl());
        if (!enc) return payload;
        return { ...payload, encryptedResources: enc };
    }

    // ---- inbox / listing -------------------------------------------------

    // Legacy callers pass { orderBy, offset, limit, hideArchived, channel } and
    // read { data, totalCount }. New inbox is a POST with pageIndex/pageSize.
    getItemsBy(params: any = {}): Promise<any> {
        const pageSize = params.limit || 20;
        const body = {
            pageIndex: toPageIndex(params.offset, pageSize),
            pageSize,
            orderBy: "Sticky",
            isSticky: params.isSticky !== undefined ? params.isSticky : null,
            isSeen: params.seen !== undefined ? params.seen : null,
            hideArchived:
                params.hideArchived !== undefined ? params.hideArchived : true,
            channels: params.channel || DEFAULT_CHANNELS,
        };
        return this.request
            .post(this.url("my-notifications"), body)
            .then((res: any) => ({
                data: (res && res.data) || [],
                totalCount: (res && res.total) || 0,
                totalUnread: (res && res.totalUnseen) || 0,
            }));
    }

    getNotifications(params?: any) {
        return this.getItemsBy(params || {});
    }

    getArchived(params?: any): any {
        return this.getItemsBy({ ...(params || {}), hideArchived: false });
    }

    // Unread badge count comes off the inbox response now (no dedicated count endpoint).
    countAllNotificationsUnRead(_params?: any) {
        return this.request
            .post(this.url("my-notifications"), {
                pageIndex: 1,
                pageSize: 1,
                orderBy: "Sticky",
                isSticky: null,
                isSeen: null,
                hideArchived: true,
                channels: DEFAULT_CHANNELS,
            })
            .then((res: any) => (res && res.totalUnseen) || 0);
    }

    // ---- read / archive state -------------------------------------------

    markAsReadForAll() {
        return this.request.post(this.url("seen"), { notificationIds: [] });
    }
    markAsReadForOne(notificationId?: any) {
        return this.request.post(this.url("seen"), {
            notificationIds: [notificationId],
        });
    }
    markAsReadForMultiple(notificationIds: Array<string>) {
        return this.request.post(this.url("seen"), { notificationIds });
    }
    markAsArchived() {
        return this.request.post(this.url("archived"), { notificationIds: [] });
    }
    createArchived(id: any) {
        return this.request.post(this.url("archived"), {
            notificationIds: [id],
        });
    }

    // ---- management (creator) list & stats ------------------------------

    // `context` is required on every tenant-scoped call so no caller can omit it and fall
    // into an ambiguous default. `"ambient"` returns the url unchanged — the request rides
    // whatever gateway gl-commonui's own current-site/token resolution already sends it to.
    // `"selectedRegion"` builds an explicit `__gl_region__` override via `resolveOwnerRcc()`,
    // throwing if the tenant doesn't resolve to a routable, actually-configured gateway.
    private withOwnerRegion(url: string, context: NotificationRoutingContext): string {
        if (context.mode === "ambient") {
            return url;
        }
        const rcc = resolveOwnerRcc(context.tenantId);
        if (rcc === undefined) {
            throw new Error(
                "Notification request blocked: no routable owner tenant for this context."
            );
        }
        return appendRegionOverride(url, rcc);
    }

    // Manage Notifications has no region-level filter — `context` only drives the
    // `__gl_region__` gateway override via `withOwnerRegion()`; the body never carries a
    // `regionId`.
    manageNotifications(params: any = {}, context: NotificationRoutingContext) {
        const pageSize = params.limit || 20;
        const body: any = {
            pageIndex: toPageIndex(params.offset, pageSize),
            pageSize,
            filterByCreator: !!params.filterByCreator,
            filterByOther: !!params.filterByOther,
        };
        return this.request
            .post(this.withOwnerRegion(this.url("manage-notifications"), context), body)
            .then((res: any) => ({
                data: (res && res.notifications) || [],
                totalCount: (res && res.totalCount) || 0,
                extraData: {
                    mineCount: (res && res.myTotalNotifications) || 0,
                },
            }));
    }

    getNotificationStatistics(id: any, context: NotificationRoutingContext) {
        return this.request
            .get(this.withOwnerRegion(this.url(`${id}/statistics`), context), null)
            .then((res: any) => ({
                totalSeen: (res && res.totalSeen) || 0,
                totalTargetUsers: (res && res.totalUsers) || 0,
            }));
    }

    // ---- target resolution (admin gateway) ------------------------------

    // Target count never moved off the admin service — the form's own roleTargets go out
    // as a bare array and { roleTargets, notifiedUserCount, users, targetCountHash } comes
    // straight back, no field translation. The absolute "~admin" path is needed because
    // serviceRoute.prefix now points at the notification service; the base create() is
    // called directly since create() below is overridden for the notification payload.
    getTargetCount(targets: any): any {
        return super.create("~admin/v1/notifications/targetcount", targets);
    }

    // Defensively unwraps a `{ data: [...] }` envelope the same way other admin-service
    // list endpoints do.
    getRegions(params?: any) {
        return this.get("~admin/v1/notifications/regions", params).then(
            (res: any) => (Array.isArray(res) ? res : (res && res.data) || [])
        );
    }

    // ---- detail / create / update / delete ------------------------------

    // The notification-service detail is a flat NotificationDetail (scope +
    // roleScopes/userScopes) — rebuilt here into the legacy { notification, notifiedCount }
    // shape the pages consume. User-scoped notifications resolve their ids back to
    // name/email so the form can render them.
    async getDetail(params: any, context: NotificationRoutingContext): Promise<any> {
        const id = params && params.id ? params.id : params;
        const detail: any = await this.request.get(this.withOwnerRegion(this.url(""), context), {
            notificationId: id,
        });
        const scope = detail.scope;
        let roleTargets: any[] = [];

        if (scope === SCOPE_USERS) {
            const userIds: string[] = detail.userScopes || [];
            const users = await this.getUsersByIds(userIds);
            // Every target here belongs to this ONE notification — its own owner tenant
            // (resolved by the caller's routing context) is the tenant source, never the
            // Account lookup's own per-user tenantId (unreliable — see
            // hydrateUserTargets()'s own doc). "ambient" mode (the read-only inbox/bell
            // detail view) has no owner-tenant concept, so tenantId is left unresolved
            // there — that view never reads it.
            const ownerTenantId = context.mode === "selectedRegion" ? context.tenantId : undefined;
            roleTargets = hydrateUserTargets(userIds, users, ROLE_INDIVIDUAL_USER, TARGET_TYPE_INDIVIDUAL_USER, ownerTenantId);
        } else {
            roleTargets = (detail.roleScopes || []).map((rs: any) => ({
                ...this.scopeToTarget(rs),
                isCalculated: true,
            }));
        }

        const notification = {
            id: detail.id,
            title: detail.title,
            description: detail.description,
            startDate: detail.startDate,
            endDate: detail.endDate,
            sticky: detail.sticky,
            channel: detail.channel,
            notificationType: scope,
            isAdvanced: detail.isAdvanced,
            advancedDescription: detail.advancedDescription,
            status: detail.status,
            createdBy: detail.generateBy,
            roleTargets,
        };
        return {
            notification,
            notifiedCount: (detail.userScopes && detail.userScopes.length) || 0,
        };
    }

    // CREATE always routes to the gateway of its resolved TARGET tenant, regardless of the
    // current site or which Notification Management list filter is active.
    // `resolveWriteRcc()` is the single source of truth and fails closed (undefined) for
    // anything but a single, fully-resolved, site-reachable tenant with a configured
    // gateway — this is defense-in-depth alongside the UI's own evaluateSubmitGate()
    // re-check (notificationfill.tsx's onSubmit()).
    private buildWriteUrl(targets: any[]): string {
        const rcc = resolveWriteRcc(targets || []);
        if (rcc === undefined) {
            throw new Error(
                "Notification write blocked: targets do not resolve to a single routable tenant."
            );
        }
        return appendRegionOverride(this.url(""), rcc);
    }

    create(values: any): Promise<any> {
        return this.request.post(this.buildWriteUrl(values.roleTargets), this.buildWritePayload(values));
    }

    // Edit always force-routes to the explicit owner tenant, never derived from the
    // (possibly-changed) target — a target that no longer matches the owner is blocked as
    // `mismatch` upstream (notificationfill.tsx) before this is ever called, rather than
    // silently moving the record between tenants.
    update(values: any, context: NotificationRoutingContext, _routeData?: any): Promise<any> {
        return this.request.put(this.withOwnerRegion(this.url(""), context), {
            NotificationId: values.id,
            ...this.buildWritePayload(values),
        });
    }

    delete(id: any, context: NotificationRoutingContext): Promise<any> {
        return this.request.del(this.withOwnerRegion(this.url(""), context), { notificationId: id });
    }

    // Kept for the (currently unrouted) review screens so they still type-check. No
    // approval workflow exists in the new service — publishing happens on create — so this
    // maps to a plain update.
    review(model: any, context: NotificationRoutingContext): Promise<any> {
        return this.update(model, context, { id: model.id });
    }

    // ---- helpers ---------------------------------------------------------

    // Individual-user targets arrive from the notification service as bare ids,
    // so names and emails are resolved through the account service the same way
    // the notification form resolves the recipients of a target count.
    private async getUsersByIds(userIds: string[]): Promise<any[]> {
        if (!userIds || !userIds.length) {
            return [];
        }
        const pending = [...userIds];
        const requests: Promise<any>[] = [];
        while (pending.length > 0) {
            requests.push(
                this.userService.getItemsBy({
                    ids: pending.splice(0, USER_LOOKUP_BATCH_SIZE),
                })
            );
        }
        const responses = await Promise.all(requests);
        return responses.reduce(
            (all: any[], res: any) => all.concat((res && res.data) || []),
            []
        );
    }

    private scopeToTarget = (rs: any) => ({
        role: Number(rs.role),
        targetType:
            rs.scopeType === null || rs.scopeType === undefined
                ? 0
                : Number(rs.scopeType),
        targetId: rs.scopeId,
        targetName: rs.scopeName,
        regionId: rs.regionId,
        schoolId: rs.schoolId,
        campusId: rs.campusId,
        classId: rs.classId,
    });

    // BE rejects `scopeType: 0` outright — `scopeType` must be `null` or a valid non-zero
    // enum value. UI's Target Type `All` (0) only maps to `scopeType: null` (mirroring
    // scopeToTarget()'s own null->All READ mapping above) for a fixed `.com`/`.us` role
    // whitelist (`isAllTargetTypeSupportedForRole()` in model.ts — the same shared source
    // of truth notificationfill.logic.ts's UI validation uses). Genuinely on `.ru` (a full
    // isolated mode), EVERY role's Target Type=All maps to `scopeType: null`
    // unconditionally, since a Russia admin's targets have no real cross-tenant/cross-role
    // ambiguity to reject. Anywhere else, a non-whitelisted role throws as defense-in-depth
    // — notificationfill.tsx's evaluateSubmitGate() already blocks this before create()/
    // update() is called, but this never silently sends a `scopeType` BE is guaranteed to
    // reject.
    private resolveWriteScopeType(rt: any): number | null {
        const targetType = Number(rt.targetType);
        if (targetType !== NotificationTargetTypes.All) {
            return toNumberOrNull(rt.targetType);
        }
        const isRussia = isCurrentSiteRussia();
        if (isRussia || isAllTargetTypeSupportedForRole(Number(rt.role))) {
            logNotificationTenantDebug("resolveWriteScopeType", {
                role: rt.role,
                targetType: rt.targetType,
                isRussia,
                mappedScopeType: null,
            });
            return null;
        }
        logNotificationTenantDebug("resolveWriteScopeType", {
            role: rt.role,
            targetType: rt.targetType,
            isRussia,
            blocked: true,
        });
        throw new Error(
            "Notification write blocked: Target Type 'All' is not a supported scope for this role."
        );
    }

    // Translate the form's legacy model into the new create/update payload.
    private buildWritePayload(values: any) {
        const scope = values.notificationType;
        const targets = values.roleTargets || [];
        const roleScopes =
            scope === SCOPE_ROLES
                ? targets.map((rt: any) => ({
                      role: Number(rt.role),
                      scopeType: this.resolveWriteScopeType(rt),
                      scopeId: rt.targetId,
                      scopeName: rt.targetName,
                      regionId: rt.regionId || null,
                      schoolId: rt.schoolId || null,
                      campusId: rt.campusId || null,
                      classId: rt.classId || null,
                  }))
                : [];
        const userScopes =
            scope === SCOPE_USERS
                ? targets.map((u: any) => ({
                      userId: u.id || u.targetId,
                      tenantId: u.tenantId != null ? u.tenantId : 0,
                  }))
                : [];
        return {
            title: values.title,
            description: values.description,
            isAdvanced: !!values.isAdvanced,
            advancedDescription: values.advancedDescription,
            startDate: values.startDate,
            endDate: values.endDate,
            scope,
            sticky: !!values.sticky,
            status: 0,
            channel: values.channel,
            roleScopes,
            userScopes,
            targetCount: values.targetCount != null ? values.targetCount : 0,
        };
    }
}
